import React, { memo, MouseEventHandler, ReactNode } from 'react'
import Grid from '@mui/material/Grid'
import ButtonBase from '@mui/material/ButtonBase'
import ImageIcon from '@mui/icons-material/Image'
import LockIcon from '@mui/icons-material/Lock'
import PublicIcon from '@mui/icons-material/Public'
import clsx from 'clsx'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import Link from 'components/Link'
import Button from 'components/Button'
import Typography from 'components/Typography'
import emptyThumbnail from 'assets/images/empty-thumbnail.svg'
import { NameUtils } from 'utils/TextUtils'
import { InformationDialog } from 'duck/AppDuck/DialogDuck'
import { CollectionCardProps } from '.'

import styles from './CollectionCard.module.scss'

type CollectionCardContentProps = Pick<
  CollectionCardProps,
  'addDialog' | 'currentUserId' | 'isAuthorized' | 'to' | 'toggleCollectionBookmark' | 'variant'
> & {
  collectionThumbnail?: string
  collectionId: number
  collectionName: string
  collectionTotal: number
  isBookmarked: boolean
  isCollectionOwner: boolean
  isPrivate: boolean
  onClick: MouseEventHandler
  TagsComponent?: ReactNode
  username: string
  userUrl: string
}

const CollectionCardContent: React.FC<CollectionCardContentProps> = props => {
  const {
    addDialog,
    collectionThumbnail,
    collectionId,
    collectionName,
    collectionTotal,
    currentUserId,
    isAuthorized,
    isBookmarked,
    isCollectionOwner,
    isPrivate,
    onClick,
    TagsComponent,
    to,
    toggleCollectionBookmark,
    username,
    userUrl,
    variant
  } = props

  return (
    <>
      <div
        className={clsx(
          'relative h-full text-0 leading-none',
          !collectionThumbnail && styles.CollectionCardImgWrapperEmpty
        )}
      >
        <Grid
          className="pointer-events-none absolute right-0 top-0 z-1 -m-1 flex p-2"
          justifyContent="flex-end"
          spacing={1}
          container
        >
          {TagsComponent}
        </Grid>

        <ButtonBase
          className={clsx(
            'block h-full w-full bg-light bg-cover bg-center bg-no-repeat',
            variant === 'light' && 'bg-primary',
            !collectionThumbnail && styles.CollectionCardImgEmpty
          )}
          style={{
            backgroundImage: `url(${collectionThumbnail || emptyThumbnail})`
          }}
          onClick={onClick}
        >
          {to && <Link className="absolute inset-0 z-1" to={to}></Link>}
        </ButtonBase>
      </div>

      <div
        className={clsx(
          'absolute inset-x-0 bottom-0 z-1 bg-primary',
          variant === 'light' && 'bg-light'
        )}
      >
        <div className="relative z-2 leading-none">
          <div className="pr-9">
            <ButtonBase onClick={onClick} className="w-full justify-start py-2 pl-2">
              <Typography variant="body1" maxLine={2}>
                {collectionName}
              </Typography>
            </ButtonBase>

            {isAuthorized !== false && !isCollectionOwner ? (
              <div className="absolute right-0.5 top-0.5 z-1">
                <Button
                  color="secondary"
                  className="min-h-9 min-w-9 text-base"
                  onClick={e => {
                    e.stopPropagation()
                    if (currentUserId) {
                      toggleCollectionBookmark?.({ collectionId: collectionId })
                    } else {
                      addDialog?.({
                        [InformationDialog.LOGIN_REQUIRED]: {
                          dialogName: InformationDialog.LOGIN_REQUIRED
                        }
                      })
                    }
                  }}
                  size="small"
                  icon
                >
                  {isBookmarked ? (
                    <BookmarkIcon fontSize="inherit" />
                  ) : (
                    <BookmarkBorderIcon fontSize="inherit" />
                  )}
                </Button>
              </div>
            ) : null}
          </div>

          <ButtonBase onClick={onClick} className="w-full justify-start px-2 pb-2 text-left">
            <Grid className="text-white-med-on-dark" alignItems="center" component="span" container>
              <Grid component="span" className="flex items-center" xs item>
                <Grid component="span" className="flex items-center" xs="auto" item>
                  {isPrivate ? (
                    <LockIcon className="mr-1 align-middle text-xs" />
                  ) : (
                    <PublicIcon className="mr-1 align-middle text-xs" />
                  )}
                </Grid>

                <Grid component="span" xs item zeroMinWidth>
                  <Typography variant="overline" className="block">
                    <Link target="_blank" color="light" to={userUrl}>
                      {NameUtils.getSlicedName(NameUtils.splitName(username), 15)}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>

              <Grid component="span" xs="auto" item>
                <div className="flex items-center text-right">
                  <ImageIcon className="mr-1 align-middle text-xs" />
                  <Typography variant="overline" className="block">
                    {collectionTotal}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </ButtonBase>
        </div>
      </div>
    </>
  )
}

export default memo(CollectionCardContent)
